import capitalize from 'lodash/capitalize';
import { getPlanTrialLabelData } from '../../../../subscriptionPlans/planLabelData';
import {
  getDurationMonths,
  getLowestPricedPlan,
  getPricePercentDecrease,
  getPricePerMonth,
  getPricePerMonthFixed,
  hasMSRPPrice,
  isMonthlyPlan,
} from './planUtils';

const MINERVA_ANNUAL_ONDEMAND_100_VS_STANDARD_QUARTERLY =
  'minerva_annual_ondemand_100_vs_standard_quarterly';

const planToNameMapping = {
  minerva_annual_ondemand_100: 'Annual Plan',
  minerva_quarterly_ondemand_41: 'Quarterly Plan',
  quarterly_7trial_41_restricted: 'App Only',
  [MINERVA_ANNUAL_ONDEMAND_100_VS_STANDARD_QUARTERLY]: 'App + Classes',
  minerva_24month_ondemand_100: 'App + Classes',
  Annual_7DayTrial_6999_restricted: 'App Only',
  minerva_annual_ondemand_90_7trial: 'Annual Plan',
  minerva_annual_ondemand_90_14trial: 'Annual Plan',
  minerva_monthly_ondemand_15_7trial: 'Monthly Plan',
};

export function getPlanDisplayName({ planDataKey, descriptionKey }) {
  return planToNameMapping[planDataKey] || capitalize(descriptionKey);
}

const hasMultipleChildProfiles = ({ maxChildProfiles }) => maxChildProfiles > 1;

// Specific to the restricted annual only plan without classes or any other special additions
function getRestrictedAnnualOnlyData({ plan }) {
  const durationInMonths = getDurationMonths(plan.subscriptionDuration);
  const { startTrialFreeLabel2 } = getPlanTrialLabelData(plan);

  const { price, parentPortalEnabled } = plan;

  return {
    plan,
    title: 'App Only',
    eyebrow: 'STANDARD',
    priceDisplay: `$${getPricePerMonthFixed(plan)}/mo`,
    billingLegal: `$${price} billed every ${durationInMonths} months`,
    valuePropsMapping: {
      access: true,
      worksheets: true,
      prerecordedLessons: false,
      parentPortal: parentPortalEnabled,
      progressReports: parentPortalEnabled,
      multipleChildProfiles: hasMultipleChildProfiles(plan),
    },
    // valueProps and showValuePropsInitially are used in mobile view only
    valueProps: ['Unlimited access to codeSpark app', 'Fun printable worksheets'],
    showValuePropsInitially: false,
  };
}

function getAnnualAndOndemandData({ plan }) {
  const durationInMonths = getDurationMonths(plan.subscriptionDuration);
  const { startTrialFreeLabel2 } = getPlanTrialLabelData(plan);

  const { price, parentPortalEnabled } = plan;

  return {
    plan,
    title: 'App + On-Demand <br />Classes',
    eyebrow: 'PREMIUM',
    priceDisplay: `$${getPricePerMonthFixed(plan)}/mo`,
    billingLegal: `$${price} billed every ${durationInMonths} months`,
    highlightText: 'MOST POPULAR',
    valuePropsMapping: {
      access: true,
      worksheets: true,
      prerecordedLessons: true,
      parentPortal: parentPortalEnabled,
      progressReports: parentPortalEnabled,
      multipleChildProfiles: hasMultipleChildProfiles(plan),
    },
    // valueProps and showValuePropsInitially are used in mobile view only
    valueProps: [
      '10 pre-recorded classes taught by experts',
      'Unlimited access to codeSpark app',
      'Fun printable worksheets',
    ],
    showValuePropsInitially: true,
  };
}

function getPremiumData({ plan, baseComparisonPlan }) {
  const { planId, price } = plan;

  const planLabelData = getPlanTrialLabelData(plan);
  const pricePerMonth = getPricePerMonth(plan);
  const hasComparisonPlan = planId !== baseComparisonPlan.planId;
  const basePlanPricePerMonth = hasComparisonPlan && getPricePerMonth(baseComparisonPlan);
  const percentChange =
    hasComparisonPlan && getPricePercentDecrease(basePlanPricePerMonth, pricePerMonth);

  return {
    plan,
    title: `${getPlanDisplayName(plan)} Plan`,
    priceDisplay: `$${getPricePerMonthFixed(plan)}/mo`,
    comparisonPlanPriceDisplay:
      hasComparisonPlan && `$${getPricePerMonthFixed(baseComparisonPlan)}/mo`,
    trial: planLabelData.startTrialFreeLabel2,
    billingLegal: `$${price} billed annually`,
    percentChange,
    showGlitch: false,
    flagText: 'Includes Coding Classes!',
    isHighlighted: true,
  };
}

function getMinervaClassesData(...args) {
  const { plan } = args[0];
  const { price, subscriptionDuration } = plan;

  const durationInMonths = getDurationMonths(subscriptionDuration);
  const { trialQuantity, trialIntervalCapitalize, trialLabelLower } = getPlanTrialLabelData(plan);
  const billingDurationText = isMonthlyPlan(plan) ? 'month' : `${durationInMonths} months`;

  return {
    ...getPremiumData(...args),
    title: getPlanDisplayName(plan),
    billingLegal: `$${price.toFixed(2)} billed every ${billingDurationText}`,
    trial: `${trialQuantity}-${trialIntervalCapitalize} Free Trial`,
    planIncludes: [
      `<b>Includes ${trialLabelLower}!</b>`,
      'App + pre-recorded classes taught by experts',
    ],
  };
}

function getMinervaPremiumVsStandardData(...args) {
  const { plan } = args[0];
  const { price, subscriptionDuration, parentPortalEnabled } = plan;

  const durationInMonths = getDurationMonths(subscriptionDuration);

  const pricePerMonth = getPricePerMonth(plan);
  const msrpPlan = {
    subscriptionDuration,
    // originalPrice is the msrp price
    price: plan.originalPrice,
  };
  const msrpPricePerMonth = hasMSRPPrice(plan) && getPricePerMonth(msrpPlan);
  const percentChange =
    !!msrpPricePerMonth && getPricePercentDecrease(msrpPricePerMonth, pricePerMonth);

  return {
    plan,
    title: getPlanDisplayName(plan),
    priceDisplay: `$${pricePerMonth}/mo`,
    comparisonPlanPriceDisplay: msrpPricePerMonth && `$${getPricePerMonthFixed(msrpPlan)}/mo`,
    percentChange,
    showGlitch: false,
    eyebrowHeader: 'STANDARD',
    billingLegal: `$${price.toFixed(2)} billed every ${durationInMonths} months`,
    valuePropsMapping: {
      access: true,
      workbook: true,
      selfPacedLessons: false,
      parentPortal: parentPortalEnabled,
      progressReports: parentPortalEnabled,
      multipleChildProfiles: hasMultipleChildProfiles(plan),
    },
  };
}

function redesignAnnualMinervaClassesData(...args) {
  return {
    ...getMinervaClassesData(...args),
    showGlitch: true,
  };
}

const planTypeToDataFunc = {
  Annual_7DayTrial_7999_restricted: getRestrictedAnnualOnlyData,
  minerva_annual_ondemand_120_7trial: getAnnualAndOndemandData,
  minerva_annual_ondemand_100: redesignAnnualMinervaClassesData,
  minerva_quarterly_ondemand_41: getMinervaClassesData,
  quarterly_7trial_41_restricted: getMinervaPremiumVsStandardData,
  [MINERVA_ANNUAL_ONDEMAND_100_VS_STANDARD_QUARTERLY]: (...args) => {
    const { plan } = args[0];
    const data = getMinervaPremiumVsStandardData(...args);
    const { valuePropsMapping = {} } = data;
    return {
      ...data,
      title: getPlanDisplayName({
        ...plan,
        planDataKey: MINERVA_ANNUAL_ONDEMAND_100_VS_STANDARD_QUARTERLY,
      }),
      showGlitch: true,
      eyebrowHeader: 'PREMIUM',
      // Once we get planType set up in database, we can use that instead of hardcoding manually here
      // to determine whether plan has classes
      valuePropsMapping: {
        ...valuePropsMapping,
        selfPacedLessons: true,
      },
    };
  },
  Annual_7DayTrial_6999_restricted: (...args) => {
    const data = getMinervaPremiumVsStandardData(...args);
    return {
      ...data,
      billingLegal: `1 Year Plan. ${data.billingLegal}`,
    };
  },
  minerva_24month_ondemand_100: (...args) => {
    const data = getMinervaPremiumVsStandardData(...args);
    const { valuePropsMapping = {} } = data;
    return {
      ...data,
      showGlitch: true,
      eyebrowHeader: 'PREMIUM',
      // Once we get planType set up in database, we can use that instead of hardcoding manually here
      // to determine whether plan has classes
      valuePropsMapping: {
        ...valuePropsMapping,
        selfPacedLessons: true,
      },
      billingLegal: `2 Year Plan. ${data.billingLegal}`,
    };
  },
  minerva_annual_ondemand_90_7trial: redesignAnnualMinervaClassesData,
  minerva_monthly_ondemand_15_7trial: getMinervaClassesData,
  minerva_annual_ondemand_90_14trial: redesignAnnualMinervaClassesData,
};

// Allow plans used in multiple experiments to map to different keys so we can have different
// copy for each experiment - similar to FM-2363 for homer
export const planDataKeyOverrides = {
  premiumVsStandardDesign: {
    minerva_annual_ondemand_100: MINERVA_ANNUAL_ONDEMAND_100_VS_STANDARD_QUARTERLY,
  },
};

export const getPlanDataKeyExperimentOverrides = experiment =>
  planDataKeyOverrides[experiment] || {};

const DEFAULT_PLAN_TYPE_FUNC = planTypeToDataFunc.Annual_7DayTrial_7999_restricted;

export function getPlansDisplayData({ planOptions, baseComparisonPlan, planDataKeyOverrides }) {
  if (!baseComparisonPlan) {
    // if no base comparison plan is provided, use the lowest priced plan in the planOptions
    // Could be monthly, quarterly types of plans
    baseComparisonPlan = getLowestPricedPlan(planOptions);
  }

  return planOptions.map(plan => {
    const { planDataKey, subscriptionDuration } = plan;
    // Allowing overrides for different experiments that use the same plan
    const planDataKeyOverride = planDataKeyOverrides[planDataKey];

    const func =
      planTypeToDataFunc[planDataKeyOverride] ||
      planTypeToDataFunc[planDataKey] ||
      planTypeToDataFunc[subscriptionDuration] ||
      DEFAULT_PLAN_TYPE_FUNC;

    return func({
      plan,
      baseComparisonPlan,
    });
  });
}



// WEBPACK FOOTER //
// ./src/containers/Home/sections/PaymentModule/plansDisplayData.js